import { Group } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { disposeObjectTree } from '../core/dispose';

export type ModelReader = (url: string, signal: AbortSignal) => Promise<Group>;
const readModel: ModelReader = async (url, signal) => {
  const response = await globalThis.fetch(url, { signal });
  if (!response.ok) throw new Error(`Model request failed: ${response.status}`);
  const data = await response.arrayBuffer();
  signal.throwIfAborted();
  return (await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).parseAsync(data, '')).scene;
};

/** Ownership passes to the scene only after a successful, still-live load. */
export class ModelAssets {
  private controller = new AbortController();
  private disposed = false;
  private states: Record<string, 'loading' | 'ready' | 'failed'> = {};
  constructor(private read: ModelReader = readModel) {}
  async load(name: string, url: string, install: (root: Group) => void): Promise<void> {
    if (this.disposed) return;
    this.states[name] = 'loading';
    let model: Group | undefined;
    try {
      model = await this.read(url, this.controller.signal);
      if (this.disposed) { disposeObjectTree(model); return; }
      install(model); this.states[name] = 'ready';
    } catch (error) {
      if (model) disposeObjectTree(model);
      if (!this.disposed) { this.states[name] = 'failed'; console.warn(`Could not load ${name}`, error); }
    }
  }
  get stats() { return { models: { ...this.states } }; }
  dispose() { this.disposed = true; this.controller.abort(); }
}
